// The asset drop zone (spec/dashboard.md section 7.3 "Look"): the current image at a readable size, drop an image on it to
// replace it or click (Enter / Space) to browse — both upload through useAssetUpload and point the field at the new asset —
// then "Pick existing…" revealing the asset combobox inline, and Clear. Used by the offers workspace and the New offer wizard.
import { useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import { useAssetUrls, useBranchAssetCtx } from '../../api/assetUrls';
import type { Json, Parameter } from '../../api/types';
import { Button } from '../../ui/Button';
import { Image as ImageIcon } from '../../ui/icons';
import { useContent } from '../ContentContext';
import { AssetPicker, useAssetUpload } from './AssetPicker';

export interface AssetDropFieldProps {
  param: Parameter;
  value: Json | undefined;
  onCommit: (value: Json) => void;
  disabled?: boolean;
  owner?: string;
  /** the zone's height: `m` (the art grid), `s` (a compact form) */
  size?: 's' | 'm';
  id?: string;
}

export function AssetDropField({ param: p, value, onCommit, disabled, owner, size = 'm', id }: AssetDropFieldProps) {
  const catalog = useContent();
  const { upload, uploading } = useAssetUpload(p);
  const fileInput = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [picking, setPicking] = useState(false);
  const current = typeof value === 'string' ? value : null;
  const asset = current ? catalog.assets.get(current) : undefined;
  const src = useAssetUrls(useBranchAssetCtx(), asset?.contentPath && asset.kind === 'image' ? [asset.id] : [])[asset?.id ?? ''];
  const label = p.displayName ?? p.name;
  const take = async (file: File | undefined) => {
    if (!file || disabled) return;
    const assetId = await upload(file);
    if (assetId) onCommit(assetId);
  };
  const browse = () => {
    if (!disabled) fileInput.current?.click();
  };
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      browse();
    }
  };
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setOver(false);
    void take(e.dataTransfer?.files?.[0]);
  };
  return (
    <div className={`asset-drop asset-drop-${size}`} data-testid={`asset-drop-${p.name}`}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={`${label}: ${asset ? asset.name : 'no asset'}. Drop an image here or press Enter to browse`}
        aria-disabled={disabled || undefined}
        className={`asset-drop-zone ${over ? 'over' : ''} ${src ? 'has-image' : ''}`}
        onClick={browse}
        onKeyDown={onKey}
        onDragEnter={(e) => {
          e.preventDefault();
          if (!disabled) setOver(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
      >
        {src ? (
          <img src={src} alt={asset?.name ?? label} />
        ) : (
          <span className="asset-drop-empty">
            <ImageIcon aria-hidden="true" />
            <span>{uploading ? 'Uploading…' : asset ? asset.name : disabled ? 'No asset' : 'Drop an image or click to browse'}</span>
          </span>
        )}
        {uploading && src && <span className="spinner asset-drop-spinner" aria-label="Uploading" />}
      </div>
      <input ref={fileInput} type="file" hidden onChange={(e) => { void take(e.target.files?.[0]); e.target.value = ''; }} aria-label={`Upload for ${label}`} />
      <div className="asset-drop-row">
        <span className="muted small asset-drop-name" title={asset?.name}>
          {asset ? asset.name : 'No asset'}
        </span>
        {!disabled && (
          <>
            <Button size="s" variant="ghost" onClick={() => setPicking((v) => !v)} aria-expanded={picking}>
              Pick existing…
            </Button>
            {current && (
              <Button size="s" variant="ghost" onClick={() => onCommit(null)}>
                Clear
              </Button>
            )}
          </>
        )}
      </div>
      {picking && !disabled && (
        <div
          className="asset-drop-picker"
          onKeyDown={(e) => {
            if (e.key === 'Escape') setPicking(false);
          }}
        >
          <AssetPicker
            param={p}
            value={value}
            owner={owner}
            id={id}
            onCommit={(v) => {
              onCommit(v);
              setPicking(false);
            }}
          />
        </div>
      )}
    </div>
  );
}
